import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useI18n, LANGS } from "../i18n";
import { ChevronDown, Bell, Menu, X } from "lucide-react";
import Logo from "./Logo";
import { api } from "../lib/api";
import { getStoredUser } from "./RegisterModal";
import UserStatusCard from "./UserStatusCard";

export default function Navbar() {
  const { t, lang, setLang } = useI18n();
  const [open, setOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const ddRef = useRef(null);
  const location = useLocation();

  const links = [
    { to: "/", label: t.nav.home, testid: "nav-home" },
    { to: "/mercado", label: t.nav.market, testid: "nav-market" },
    { to: "/comparador", label: t.nav.comparator, testid: "nav-comparator" },
    { to: "/simulador", label: t.nav.simulator, testid: "nav-simulator" },
    { to: "/geografia", label: t.nav.geography, testid: "nav-geography" },
    { to: "/assistente", label: t.nav.ai, testid: "nav-ai" },
    { to: "/coach", label: (
      lang === "pt" ? "Coaching Estratégico" :
      lang === "es" ? "Coaching Estratégico" :
      lang === "fr" ? "Coaching Stratégique" :
      lang === "de" ? "Strategisches Coaching" :
      lang === "zh" ? "战略教练" :
      lang === "ja" ? "戦略コーチング" :
      lang === "hi" ? "रणनीतिक कोचिंग" :
      "Strategic Coaching"
    ), testid: "nav-coach" },
    { to: "/watchlist", label: lang === "pt" ? "Watchlist" : "Watchlist", testid: "nav-watchlist" },
    { to: "/perfil", label: t.nav.profile, testid: "nav-profile" },
    { to: "/glossario", label: t.nav.glossary, testid: "nav-glossary" },
    { to: "/planos", label: t.nav.pricing, testid: "nav-pricing" },
  ];

  useEffect(() => {
    const handler = (e) => {
      if (ddRef.current && !ddRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Close mobile drawer on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Lock body scroll when drawer open
  useEffect(() => {
    if (mobileOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  // Poll alerts count for logged-in users (silently fails for free / no user)
  useEffect(() => {
    const u = getStoredUser();
    if (!u?.email) return;
    let active = true;
    const fetchUnread = () => {
      api.get(`/alerts?email=${encodeURIComponent(u.email)}`)
        .then((r) => { if (active) setUnread(r.data.unread || 0); })
        .catch(() => {});
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 60000);
    return () => { active = false; clearInterval(t); };
  }, []);

  const current = LANGS.find((l) => l.code === lang) || LANGS[0];

  return (
    <header className="crystal fixed top-0 left-0 right-0 z-50" data-testid="site-navbar">
      <nav className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-10 h-16 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2.5 group shrink-0" data-testid="brand-logo">
          <Logo size={30} />
          <span className="font-brand text-[1.15rem] tracking-[0.14em] brand-metal">GOLDORA</span>
        </Link>

        <ul className="hidden lg:flex items-center gap-0.5 text-sm flex-1 justify-center">
          {links.map((l) => (
            <li key={l.to}>
              <NavLink
                to={l.to}
                data-testid={l.testid}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `px-2.5 py-2 rounded-md transition-all duration-200 hover:text-white ${
                    isActive
                      ? "text-white bg-white/5 border border-white/10"
                      : "text-neutral-400 border border-transparent"
                  }`
                }
              >
                {l.label}
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          {unread > 0 && (
            <Link
              to="/watchlist"
              data-testid="nav-bell"
              className="relative p-2 text-gold-300 hover:text-gold-200 transition-colors"
              aria-label="Notifications"
            >
              <Bell size={16} />
              <span className="absolute top-1 right-1 bg-gold-500 text-black text-[9px] font-bold rounded-full w-3.5 h-3.5 flex items-center justify-center">
                {unread > 9 ? "9+" : unread}
              </span>
            </Link>
          )}
          <div className="relative" ref={ddRef}>
            <button
              onClick={() => setOpen(!open)}
              data-testid="lang-switcher"
              className="flex items-center gap-1.5 px-2.5 sm:px-3 py-2 text-xs font-mono uppercase tracking-widest bg-white/5 border border-white/10 rounded-md hover:border-white/20 transition-colors"
            >
              <span className="text-base leading-none">{current.flag}</span>
              <span className="text-neutral-300">{current.code.toUpperCase()}</span>
              <ChevronDown size={12} className={`text-neutral-500 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
              <div
                className="absolute right-0 mt-2 w-56 surface-elevated p-1.5 shadow-lg rounded-md z-[60]"
                data-testid="lang-menu"
              >
                {LANGS.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => {
                      setLang(l.code);
                      setOpen(false);
                    }}
                    data-testid={`lang-${l.code}`}
                    className={`w-full text-left px-3 py-2 rounded flex items-center gap-2.5 text-sm transition-colors ${
                      lang === l.code
                        ? "bg-gold-500/15 text-gold-300"
                        : "text-neutral-300 hover:bg-white/5"
                    }`}
                  >
                    <span className="text-base leading-none">{l.flag}</span>
                    <span>{l.label}</span>
                    <span className="ml-auto font-mono text-[10px] text-neutral-500">{l.code}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-btn"
            aria-label="Open menu"
            className="lg:hidden p-2 rounded-md bg-white/5 border border-white/10 text-neutral-300 hover:text-white hover:border-white/20 transition-colors"
          >
            <Menu size={18} />
          </button>
        </div>
      </nav>

      {/* Mobile drawer — rendered via Portal to escape the header's backdrop-filter
          containing block (otherwise position:fixed gets clipped to header height) */}
      {mobileOpen && createPortal(
        <div
          className="lg:hidden fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm animate-in fade-in"
          onClick={() => setMobileOpen(false)}
          data-testid="mobile-drawer-backdrop"
        >
          <aside
            className="absolute right-0 top-0 bottom-0 w-[88%] max-w-sm bg-black border-l border-gold-500/20 shadow-[0_0_60px_rgba(233,185,52,0.08)] flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="mobile-drawer"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
              <div className="flex items-center gap-2.5">
                <Logo size={26} />
                <span className="font-brand text-[1rem] tracking-[0.14em] brand-metal">GOLDORA</span>
              </div>
              <button
                onClick={() => setMobileOpen(false)}
                aria-label="Close menu"
                data-testid="mobile-menu-close"
                className="p-2 rounded-md text-neutral-400 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto py-3">
              <UserStatusCard onNavigate={() => setMobileOpen(false)} />
              <ul className="px-2 mt-1">
                {links.map((l, i) => (
                  <li key={l.to} className="rise-in" style={{ animationDelay: `${i * 35}ms` }}>
                    <NavLink
                      to={l.to}
                      end={l.to === "/"}
                      data-testid={`mobile-${l.testid}`}
                      onClick={() => setMobileOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center justify-between px-4 py-3.5 rounded-lg my-0.5 text-base transition-colors ${
                          isActive
                            ? "bg-gold-500/10 border border-gold-500/30 text-gold-200"
                            : "text-neutral-200 hover:bg-white/5 border border-transparent"
                        }`
                      }
                    >
                      <span>{l.label}</span>
                      <span className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-600">→</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </nav>

            <div className="px-5 py-4 border-t border-white/5 text-[10px] font-mono uppercase tracking-[0.22em] text-neutral-600 text-center">
              {lang === "pt" ? "Visão global. Você no controle." : "Global vision. You in control."}
            </div>
          </aside>
        </div>,
        document.body
      )}
    </header>
  );
}
